import qs from 'query-string';

import { isSemrushMode } from '@constants/app-modes';
import { isFeatureEnabled } from '@utils/featureFlags';
import { getCookie, setCookie } from '@utils/cookies';

/**
 * First-party ad attribution capture (docs/tracking/02-technical-spec.md §2).
 *
 * Runs at module top-level in every entry file BEFORE handleRedirects() /
 * initializeIntegration(), because those destroy the query string. Captures raw
 * values only — normalization happens at report time on the backend.
 *
 * Storage is intentionally NOT localStorageHandler: its setters are async and
 * no-ops outside standalone mode, while capture must be synchronous and run in
 * all standalone realms. Values are mirrored into `.respona.com` cookies so they
 * survive across app/dashboard/publisher subdomains and can be pre-seeded by the
 * marketing site (cookie contract: docs/tracking/05-attribution-cookie-contract.md
 * in the root workspace).
 */

export const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
export const CLICK_ID_PARAMS = ['fbclid', 'gclid', 'msclkid', 'ttclid', 'li_fat_id'] as const;
export const ATTRIBUTION_PARAMS = [...UTM_PARAMS, ...CLICK_ID_PARAMS] as const;

export const FIRST_TOUCH_KEY = 'respona.attribution.first';
export const LAST_TOUCH_KEY = 'respona.attribution.last';

const COOKIE_DOMAIN = '.respona.com';
const COOKIE_DAYS = 365;

export type AttributionTouch = {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  referrer?: string;
  landingPage?: string;
  capturedAt: number;
  clickIds?: Record<string, string>;
  // ALL utm_* params raw, including non-standard ones (e.g. utm_source_id used by
  // referral links). The named fields above are the reporting dimensions; this map
  // preserves everything for the RegistrationCode.tags referral path.
  utmParams?: Record<string, string>;
};

export type Attribution = {
  firstTouch?: AttributionTouch;
  lastTouch?: AttributionTouch;
};

const isEnabled = (): boolean => !isSemrushMode() && isFeatureEnabled('ENABLE_AD_ATTRIBUTION');

const parseTouch = (json: string | null | undefined): AttributionTouch | undefined => {
  if (!json) return undefined;
  try {
    const parsed: unknown = JSON.parse(json);
    // Shape check, not just a parse guard. `[]`, `123` and `"x"` are all valid JSON, so the old
    // `as AttributionTouch` cast handed back a truthy non-touch, which counted as a present first
    // touch and permanently blocked any rewrite in that browser (UTM-EDGE-02). A corrupt value is
    // treated as absent so the next visit can re-capture.
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return undefined;
    }
    return parsed as AttributionTouch;
  } catch {
    // unreadable value is treated as absent
    return undefined;
  }
};

/**
 * Best-effort write of a cookie-sourced touch back into localStorage. Never throws:
 * a blocked or full store just leaves the cookie as the source of truth, and the
 * caller's return value is unaffected either way.
 */
const mirrorToLocalStorage = (key: string, touch: AttributionTouch): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(touch));
  } catch {
    // storage blocked; the cookie remains the source of truth
  }
};

/**
 * Read one persisted touch. For the FIRST touch, localStorage wins over the cookie:
 * the semantics are write-once, so which copy we read barely matters and preferring
 * localStorage avoids churn. For the LAST touch (`pickNewest`), both sources are read
 * and the fresher `capturedAt` wins — the marketing site writes only the shared
 * `.respona.com` cookie, so a stale localStorage value must not shadow a newer
 * cross-subdomain touch.
 *
 * Whenever the cookie is what we return — because localStorage was empty, or because
 * it held the newer touch — it is mirrored back into localStorage. Without that, a
 * cross-subdomain hop (or a cleared store) left the first touch readable only from
 * the cookie: `getAttribution()` still returned the right value, so signup transport
 * and the persisted row stayed correct, but every read re-parsed the cookie and the
 * two stores disagreed for the rest of the session (UTM-CAP-08).
 */
const readTouch = (key: string, pickNewest = false): AttributionTouch | undefined => {
  let local: AttributionTouch | undefined;
  try {
    local = parseTouch(window.localStorage.getItem(key));
  } catch {
    // storage blocked; fall through to cookie
  }

  if (local && !pickNewest) return local;

  let cookie: AttributionTouch | undefined;
  try {
    cookie = parseTouch(getCookie(key));
  } catch {
    // unreadable cookie is treated as absent
  }

  if (!local) {
    // Recovered from the shared `.respona.com` cookie: either a cross-subdomain hop
    // (marketing site -> app, app -> concierge) or a cleared localStorage. Mirror it
    // back so this origin serves subsequent reads locally and the two stores agree.
    if (cookie) mirrorToLocalStorage(key, cookie);
    return cookie;
  }
  if (!cookie) return local;

  const localAt = Number(local.capturedAt) || 0;
  const cookieAt = Number(cookie.capturedAt) || 0;
  if (cookieAt > localAt) {
    mirrorToLocalStorage(key, cookie);
    return cookie;
  }
  return local;
};

const writeTouch = (key: string, touch: AttributionTouch): void => {
  const json = JSON.stringify(touch);
  try {
    window.localStorage.setItem(key, json);
  } catch {
    // storage blocked; the cookie below may still succeed
  }
  setCookie(key, json, { domain: COOKIE_DOMAIN, days: COOKIE_DAYS });
};

const isExternalReferrer = (referrer: string): boolean => {
  if (!referrer) return false;
  try {
    return new URL(referrer).host !== window.location.host;
  } catch {
    return false;
  }
};

const composeCurrentTouch = (): { touch: AttributionTouch; hasSignal: boolean } => {
  const parsedSearch = qs.parse(window.location.search);

  const readParam = (name: string): string | undefined => {
    const value = parsedSearch[name];
    const single = Array.isArray(value) ? value[0] : value;
    return single != null && single.length > 0 ? single : undefined;
  };

  const clickIds: Record<string, string> = {};
  CLICK_ID_PARAMS.forEach((name) => {
    const value = readParam(name);
    if (value) clickIds[name] = value;
  });

  const utmParams: Record<string, string> = {};
  Object.keys(parsedSearch).forEach((name) => {
    if (name.startsWith('utm_')) {
      const value = readParam(name);
      if (value) utmParams[name] = value;
    }
  });

  const referrer = isExternalReferrer(document.referrer) ? document.referrer : undefined;

  const touch: AttributionTouch = {
    source: readParam('utm_source'),
    medium: readParam('utm_medium'),
    campaign: readParam('utm_campaign'),
    term: readParam('utm_term'),
    content: readParam('utm_content'),
    referrer,
    landingPage: window.location.pathname,
    capturedAt: Date.now(),
    clickIds: Object.keys(clickIds).length ? clickIds : undefined,
    utmParams: Object.keys(utmParams).length ? utmParams : undefined,
  };

  const hasSignal =
    Boolean(touch.utmParams) || Boolean(touch.clickIds) || Boolean(referrer);

  return { touch, hasSignal };
};

/**
 * Capture the current landing into first/last-touch storage. Synchronous, exception-safe,
 * no-op in Semrush mode and behind the ENABLE_AD_ATTRIBUTION flag.
 */
export const captureAttribution = (): void => {
  try {
    if (!isEnabled()) return;

    const { touch, hasSignal } = composeCurrentTouch();

    // The marketing site may have pre-seeded touches via the .respona.com cookie
    // contract; readTouch() already prefers localStorage and falls back to it.
    const existingFirst = readTouch(FIRST_TOUCH_KEY);

    if (hasSignal) {
      // URL params win over anything pre-seeded for the LAST touch: overwrite.
      writeTouch(LAST_TOUCH_KEY, touch);
      if (!existingFirst) writeTouch(FIRST_TOUCH_KEY, touch);
    } else if (!existingFirst) {
      // Nothing present: still record the first landing once, so "direct" is
      // distinguishable from "not captured".
      writeTouch(FIRST_TOUCH_KEY, touch);
    }
  } catch {
    // Attribution must never break app startup.
  }
};

/**
 * Read the persisted attribution for sending at signup. Returns undefined when
 * the feature is off or nothing was captured (Semrush, storage blocked everywhere).
 *
 * Reads are gated by the SAME flag as capture (QA 2026-08-28, F1): with
 * ENABLE_AD_ATTRIBUTION off, a cookie pre-seeded by the marketing site must not
 * flow into signups or GTM. The marketing site may keep writing the shared
 * cookie; this SPA neither writes nor reads it until the flag flips (TRACK-010).
 */
export const getAttribution = (): Attribution | undefined => {
  try {
    if (!isEnabled()) return undefined;

    const firstTouch = readTouch(FIRST_TOUCH_KEY);
    const lastTouch = readTouch(LAST_TOUCH_KEY, true);

    if (!firstTouch && !lastTouch) return undefined;
    return { firstTouch, lastTouch };
  } catch {
    return undefined;
  }
};

/**
 * All utm_* pairs of the freshest touch, for the referral RegistrationCode.tags path
 * (organization_referrals resolves utm_source/utm_source_id from these). Replaces the
 * render-time capture previously duplicated in LoginPage/LoginPageFlows.
 */
export const getAttributionUtmPairs = (): [string, string][] => {
  try {
    // The persisted branch is flag-gated via getAttribution(). The live query-string
    // fallback below stays UNGATED on purpose: the referral flow (RegistrationCode.tags,
    // utm_source/utm_source_id resolution) predates ad attribution and must keep working
    // with ENABLE_AD_ATTRIBUTION off, exactly as the old render-time capture did.
    const attribution = getAttribution();
    const utmParams = attribution?.lastTouch?.utmParams ?? attribution?.firstTouch?.utmParams;
    if (utmParams) return Object.entries(utmParams);

    // qs.parse values can be null (valueless `?utm_source`) or arrays (repeated params);
    // normalize to non-empty single strings so callers never feed null into pair.setValue.
    const pairs: [string, string][] = [];
    Object.entries(qs.parse(window.location.search)).forEach(([key, value]) => {
      if (!key.startsWith('utm_')) return;
      const single = Array.isArray(value) ? value[0] : value;
      if (single != null && single.length > 0) pairs.push([key, single]);
    });
    return pairs;
  } catch {
    return [];
  }
};
